/**
 * What kind of device and window we are running in.
 *
 * These questions decide whether the app can be installed, whether it already
 * has been, and whether push is possible at all — so they are asked from
 * several places and are worth having in exactly one.
 */

/**
 * An iPhone or iPad.
 *
 * The touch-points half of this is not paranoia: iPadOS 13 and later report a
 * desktop Mac user agent by choice, so an iPad is invisible to the obvious
 * check. A Mac with a touch screen does not exist, so touch points give it
 * away.
 */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Mac/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}

/** Running as an installed app rather than inside a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** An iOS browser tab, where push works only once the app is installed. */
export function isUninstalledIos(): boolean {
  return isIos() && !isStandalone();
}

/**
 * A touch device — in practice, a phone or tablet.
 *
 * Used to keep the install nudge off desktops: desktop Chrome offers
 * installation too, but desktop notifications work without it, so there the
 * banner would be noise rather than help.
 */
export function isTouchDevice(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}
